<template>
    <div ref="dropdown" v-click-outside="closeDropdown"
        :class="['custom-select-container', theme, { 'is-disabled-local': disabled }]">
        <label :class="['custom_label', { 'label_margin': label != '' }]">{{ label }}</label>
        <div class="custom-dropdown">
            <!-- Trigger del calendario — tres composiciones sobre el mismo
                chrome del system:
                  - chip single (default): un único valor + chevron.
                  - range chrome ("range_trigger"): label fuerte + valor(es) en
                    muted. Lo usan tanto el modo range (from – to) como el modo
                    single con `triggerVariant="range"` (un solo mes). Comparten
                    estilo para que un header que alterna entre rango
                    (Mensual/Trimestral) y mes único (YTD) no mute la identidad
                    visual del control: sólo cambia el valor, no el chrome. -->
            <div v-if="!isRange && triggerVariant === 'chip'"
                class="dropdown_label_select"
                :class="{ 'dropdown_label_select_clicked': isOpen }"
                @click="toggleDropdown">
                <span class="selected-label is-capitalized">
                    {{ labelDate }}
                </span>
                <ArrowDown class="actual_button_arrow_down" />
            </div>
            <button v-else
                type="button"
                class="range_trigger"
                :class="{ 'is-open': isOpen }"
                @click="toggleDropdown">
                <span class="range_trigger_label">{{ rangeTriggerLabel }}</span>
                <span class="range_trigger_dates">
                    <span v-if="isYtd" class="is-capitalized">{{ ytdTriggerLabel }}</span>
                    <template v-else-if="isRange">
                        <span class="is-capitalized">{{ rangeFromLabel }}</span>
                        <span class="range_sep" aria-hidden="true"> – </span>
                        <span class="is-capitalized">{{ rangeToLabel }}</span>
                    </template>
                    <span v-else class="is-capitalized">{{ labelDate }}</span>
                </span>
            </button>

            <!-- Menú desplegable -->
            <transition name="slide">
                <div class="dropdown-menu-select"
                    :class="{ 'is-right': alignRight && dropdownStrategy !== 'fixed' }"
                    :style="fixedStyle" v-if="isOpen">
                    <div class="calendar-container">
                        <!-- Cabecera: año + nav. Mantenemos ambos chevrons
                            siempre presentes (con `disabled` cuando no aplica)
                            para que el layout no salte al cambiar de año. -->
                        <div class="calendar-header">
                            <button type="button"
                                class="year_nav_btn"
                                :disabled="prevYearDisabled"
                                aria-label="Año anterior"
                                @click.stop="updateYear(-1)">
                                <ArrowDown class="year_nav_icon rotateLeft" />
                            </button>

                            <span class="year_label is-unselectable">{{ showYearGrid ? yearPageLabel : currentYear }}</span>
                            <button type="button"
                                class="year_nav_btn"
                                :disabled="nextYearDisabled"
                                aria-label="Año siguiente"
                                @click.stop="updateYear(1)">
                                <ArrowDown class="year_nav_icon rotateRight" />
                            </button>
                        </div>

                        <!-- Matriz de Meses (granularidad "month", o el paso
                            "month" del modo ytd) -->
                        <div v-if="!showYearGrid" class="calendar-grid" @mouseleave="onMonthLeave">
                            <button v-for="(month, index) in months" :key="index"
                                type="button"
                                class="month-cell"
                                :class="{
                                    'is-disabled-local': isMonthDisabled(index),
                                    'month-cell-activated': isMonthSelected(index),
                                    'month-cell-from': isMonthFrom(index),
                                    'month-cell-to': isMonthTo(index),
                                    'month-cell-in-range': isMonthInRange(index),
                                    'is-preview': isRange && !rangeEnd && (isMonthTo(index) || isMonthInRange(index)),
                                }"
                                :disabled="isMonthDisabled(index)"
                                @click.stop="selectMonth(index)"
                                @mouseenter="onMonthHover(index)">
                                {{ month }}
                            </button>
                        </div>

                        <!-- Matriz de Años (granularidad "year"). Reusa las
                            mismas celdas (.month-cell) y estilos de rango que la
                            grilla de meses; sólo cambia el contenido y los
                            helpers de highlight (por año, no por mes). -->
                        <div v-else class="calendar-grid" @mouseleave="onMonthLeave">
                            <button v-for="year in yearCells" :key="year"
                                type="button"
                                class="month-cell"
                                :class="{
                                    'is-disabled-local': isYearDisabled(year),
                                    'month-cell-from': isYearFrom(year),
                                    'month-cell-to': isYearTo(year),
                                    'month-cell-in-range': isYearInRange(year),
                                    'is-preview': !rangeEnd && (isYearTo(year) || isYearInRange(year)),
                                }"
                                :disabled="isYearDisabled(year)"
                                @click.stop="selectYear(year)"
                                @mouseenter="onYearHover(year)">
                                {{ year }}
                            </button>
                        </div>
                    </div>
                </div>
            </transition>

        </div>
    </div>
</template>

<script>
import ArrowDown from "@/components/svg/ArrowDown.vue";
import moment from "moment";
// Idéntico patrón que en dateRange.js: aplicamos locale explícito a los
// `moment(...)` que produzca labels para no depender del default global
// (cambia según qué componentes haya montado el caller previamente).
import "moment/locale/es";
import clickOutside from "@/directives/clickOutside";
export default {
    name: "AnualCalendar",
    components: {
        ArrowDown,
    },
    directives: { clickOutside },
    props: {
        disableOldDates: {
            type: Boolean,
            default: false
        },
        disabledFrom: {
            type: Date,
            required: false
        },
        theme: {
            type: String,
            default: 'default_theme'
        },
        label: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        value: { // La prop debe llamarse 'value' para que funcione con v-model
            type: Date,
            // En modo "range" el componente no usa `value` (lee/emite
            // `rangeValue`/`range-change`). Mantener `required: true` rompería
            // a los callers de range que ya no tienen un Date individual.
            required: false,
            default: null,
        },
        formatLabelDate: {
            type: String,
            default: 'MMM-YYYY'
        },
        // Texto del trigger en modo single cuando NO hay mes elegido
        // (`value` null): el control del mes de inicio del reporte arranca
        // vacío ("desde el primer documento"). Antes un value null pintaba
        // "Invalid date". Default "—", la misma ausencia-con-forma del range.
        placeholder: {
            type: String,
            default: '—',
        },
        disableNewerDates: {
            type: Boolean,
            default: false
        },
        // Piso INDEPENDIENTE del ancla disabledFrom (que sirve de piso O techo
        // según disableOldDates/disableNewerDates, nunca ambos): primer mes con
        // datos habilitado. Meses/años anteriores quedan deshabilitados y la
        // navegación de años no retrocede más allá. Los reportes financieros lo
        // alimentan con el primer mes con data de la empresa; null = sin piso
        // (default — nada cambia para los callers existentes).
        minDate: {
            type: Date,
            default: null,
        },
        alignRight: {
            type: Boolean,
            default: false
        },
        // Selección de un único mes (default) o rango [from, to]. El layout
        // del trigger y del grid cambian según el modo; el resto del componente
        // (theme, dropdown, click-outside) se reusa.
        mode: {
            type: String,
            default: 'single',
            // "ytd" = selector combinado de dos pasos (rango de AÑOS → mes de
            // corte) en un solo dropdown. Reusa la máquina de rango para el span
            // de años y luego una grilla de meses para el corte. Emite
            // `ytd-change` con { from, to, endMonth }.
            validator: (v) => ['single', 'range', 'ytd'].includes(v),
        },
        // Valor del modo range — `{ from: Date, to: Date }`. Si una de las dos
        // viene null, el grid arranca en el lado que sí tenga fecha y el primer
        // click setea el faltante. Lo dejamos opcional para permitir uncontrolled
        // initial state ("ningún mes seleccionado todavía").
        rangeValue: {
            type: Object,
            default: null,
        },
        // Label fuerte que precede a las fechas en el trigger del modo range.
        // En VentasTab dice "Rango"; otros tabs podrían pasar "Período" / "Mes"
        // según contexto.
        rangeTriggerLabel: {
            type: String,
            default: "Rango",
        },
        // Variante del trigger en modo single. "chip" (default) = pill con el
        // valor + chevron. "range" = reusa el chrome del trigger de rango
        // (label fuerte + valor en muted), para que un mes único pueda
        // convivir visualmente con un selector de rango en el mismo header.
        triggerVariant: {
            type: String,
            default: "chip",
            validator: (v) => ["chip", "range"].includes(v),
        },
        // Granularidad de la selección. "month" (default) = grilla de 12 meses
        // navegando año a año (selección de mes único o rango de meses).
        // "year" = grilla de 12 años navegando de a páginas de 12 (selección de
        // un rango de años; sólo aplica con mode="range"). Los endpoints de un
        // rango de años se normalizan a Ene 1 del año; el resto del componente
        // (chrome, dropdown, click-range, hover preview) se reusa tal cual.
        granularity: {
            type: String,
            default: "month",
            validator: (v) => ["month", "year"].includes(v),
        },
        // Posicionamiento del menú. "absolute" (default) lo ancla al trigger —
        // exige que ningún ancestro recorte overflow. "fixed" lo posiciona
        // contra el viewport (calculado al abrir, con flip hacia arriba si no
        // hay espacio abajo): para triggers dentro de contenedores con scroll
        // (p.ej. el body scrolleable de ChangeAccrual), donde absolute quedaría
        // recortado. En fixed, scroll/resize cierran el menú porque su posición
        // quedaría desanclada del trigger.
        dropdownStrategy: {
            type: String,
            default: "absolute",
            validator: (v) => ["absolute", "fixed"].includes(v),
        },
    },
    data() {
        // Inicializamos `rangeStart`/`rangeEnd`/`currentYear` desde la prop
        // directamente para que el PRIMER render ya tenga el estado correcto.
        // Si los seteáramos en mounted, el primer paint del template usaría
        // los defaults y los pills mostrarían "—" hasta el siguiente tick —
        // pequeño glitch visible y problema reproducible en specs sin
        // `await $nextTick()`.
        const seed = (this.mode === "range" || this.mode === "ytd") ? this.rangeValue : null;
        const fromSeed = seed && seed.from
            ? new Date(seed.from.getFullYear(), seed.from.getMonth(), 1)
            : null;
        const toSeed = seed && seed.to
            ? new Date(seed.to.getFullYear(), seed.to.getMonth(), 1)
            : null;
        const initialYear = (toSeed || fromSeed || this.value || new Date()).getFullYear();
        return {
            isOpen: false,
            currentYear: initialYear,
            // Modo "ytd": paso activo del selector combinado ("years" → "month")
            // y el índice del mes de corte (0-11), seedeado del mes de `value`
            // (= endMonth). El span de años reusa rangeStart/rangeEnd.
            ytdStep: "years",
            ytdMonth: (this.mode === "ytd" && this.value) ? this.value.getMonth() : null,
            // Primer año de la página visible de la grilla en granularidad
            // "year" (12 años por página, alineados a múltiplos de 12 para que
            // el paginado sea estable). El año en foco siempre cae dentro.
            yearPageStart: initialYear - (initialYear % 12),
            initialDate: null,
            months: [
                "Enero", "Febrero", "Marzo",
                "Abril", "Mayo", "Junio",
                "Julio", "Agosto", "Septiembre",
                "Octubre", "Noviembre", "Diciembre"
            ],
            selectedDate: null,
            // Modo range — `rangeStart`/`rangeEnd` son los dos endpoints
            // (normalizados al día 1 de cada mes). `pickingPhase` define qué
            // setea el próximo click:
            //   - "from": el próximo click reinicia el rango (nuevo start, end=null).
            //   - "to": el próximo click cierra el rango (set end) o reemplaza
            //     start si el click es anterior al actual start.
            rangeStart: fromSeed,
            rangeEnd: toSeed,
            pickingPhase: fromSeed && toSeed ? "from" : "to",
            // Preview en vivo del segundo endpoint: cuando el usuario hovea
            // un mes durante la selección parcial, el grid pinta el rango
            // futuro para que el clic sea predictivo, no a ciegas. Es el
            // signature del calendar — desplaza el modelo mental de "click +
            // verificá" a "verificá + click".
            hoveredMonth: null,
            // Estilo inline del menú en estrategia "fixed" (null en absolute).
            // Se recalcula en cada apertura desde el rect del trigger.
            fixedStyle: null,
        };
    },
    computed: {
        isRange() {
            // El modo "ytd" reusa toda la máquina de rango para el span de años.
            return this.mode === "range" || this.isYtd;
        },
        isYtd() {
            return this.mode === "ytd";
        },
        isYear() {
            return this.granularity === "year";
        },
        // Qué grilla se muestra. En "ytd" depende del paso (años → mes); en el
        // resto, de la granularidad. Reemplaza a `isYear` en el template y en la
        // navegación para soportar el cambio de grilla intra-dropdown del ytd.
        showYearGrid() {
            if (this.isYtd) return this.ytdStep === "years";
            return this.isYear;
        },
        // Label del trigger en modo ytd: "2024 – 2026 · Jun" (span de años +
        // mes de corte). "—" en cada parte aún sin elegir.
        ytdTriggerLabel() {
            const from = this.rangeStart ? this.rangeStart.getFullYear() : "—";
            const to = this.rangeEnd ? this.rangeEnd.getFullYear() : "—";
            let month = "—";
            if (this.ytdMonth != null) {
                const abbr = moment().month(this.ytdMonth).locale("es").format("MMM").replace(".", "");
                month = abbr.charAt(0).toUpperCase() + abbr.slice(1);
            }
            return `${from} – ${to} · ${month}`;
        },
        // Los 12 años de la página visible de la grilla (granularidad "year").
        yearCells() {
            return Array.from({ length: 12 }, (_, i) => this.yearPageStart + i);
        },
        // Etiqueta del header en granularidad "year": el span de la página
        // ("2016 – 2027"), en lugar del año único del modo mes.
        yearPageLabel() {
            return `${this.yearPageStart} – ${this.yearPageStart + 11}`;
        },
        labelDate() {
            if (!this.selectedDate) return this.placeholder;
            const month = moment(this.selectedDate).format(this.formatLabelDate);
            return month.replace(".", "");
        },
        // Si rangeEnd no está cerrado, este es el endpoint candidato derivado
        // del hover. `null` significa "sin preview" (rango cerrado o sin
        // hover válido). Se usa por `isMonthTo` / `isMonthInRange` para
        // pintar el preview con el mismo estilo que el rango real.
        previewEnd() {
            if (!this.isRange || !this.rangeStart || this.rangeEnd) return null;
            return this.hoveredMonth;
        },
        // `minDate` normalizado al día 1 de su mes: las celdas del grid son
        // meses (día 1), así la comparación por timestamp es exacta.
        minMonth() {
            if (!this.minDate) return null;
            return new Date(this.minDate.getFullYear(), this.minDate.getMonth(), 1);
        },
        // Disable de los chevrons: para `disableOldDates` el límite es
        // `initialDate.getFullYear()`; para `disableNewerDates` el límite es
        // el año del `initialDate` también (= "no permitas navegar a futuro").
        // `minMonth` agrega un piso propio. Sin límites, chevrons habilitados.
        prevYearDisabled() {
            // En el paso "mes" del ytd el año queda fijo al `to` del rango → sin nav.
            if (this.isYtd && this.ytdStep === "month") return true;
            // Piso por minDate: no navegar a años enteramente sin datos.
            if (this.minMonth) {
                const floorYear = this.minMonth.getFullYear();
                if (this.showYearGrid && this.yearPageStart <= floorYear) return true;
                if (!this.showYearGrid && this.currentYear <= floorYear) return true;
            }
            if (!this.disableOldDates || !this.initialDate) return false;
            // En la grilla de años el chevron retrocede una página de 12 años;
            // sólo se deshabilita si toda la página ya cae bajo el piso.
            if (this.showYearGrid) {
                return this.yearPageStart <= this.initialDate.getFullYear();
            }
            return this.currentYear <= this.initialDate.getFullYear();
        },
        nextYearDisabled() {
            if (this.isYtd && this.ytdStep === "month") return true;
            if (!this.disableNewerDates || !this.initialDate) return false;
            // En la grilla de años, deshabilitar avanzar cuando la página ya
            // muestra hasta el año tope (initialDate) — no hay años futuros.
            if (this.showYearGrid) {
                return this.yearPageStart + 11 >= this.initialDate.getFullYear();
            }
            return this.currentYear >= this.initialDate.getFullYear();
        },
        // Formato de los endpoints en el trigger de rango: "MMM YYYY" en mes,
        // sólo el año en granularidad year.
        rangeFromLabel() {
            if (!this.rangeStart) return "—";
            if (this.isYear) return String(this.rangeStart.getFullYear());
            return moment(this.rangeStart).locale("es").format("MMM YYYY").replace(".", "");
        },
        rangeToLabel() {
            if (!this.rangeEnd) return "—";
            if (this.isYear) return String(this.rangeEnd.getFullYear());
            return moment(this.rangeEnd).locale("es").format("MMM YYYY").replace(".", "");
        },
    },
    watch: {
        // Toda apertura/cierre pasa por acá (toggle, click-outside, selección
        // de mes): un solo punto ata/desata los listeners del modo fixed, sin
        // depender de qué camino cerró el menú.
        isOpen(open) {
            if (this.dropdownStrategy !== "fixed") return;
            if (open) {
                this.applyFixedPosition();
                // capture: true — el scroll de cualquier contenedor intermedio
                // (no sólo window) desancla la posición y debe cerrar.
                window.addEventListener("scroll", this.onViewportChange, true);
                window.addEventListener("resize", this.onViewportChange);
            } else {
                this.removeFixedListeners();
            }
        },
        value(newValue) {
            // En ytd `value` (= endMonth) sólo aporta el mes de corte.
            if (this.isYtd) {
                if (newValue) this.ytdMonth = newValue.getMonth();
                return;
            }
            if (this.isRange) return;
            if (newValue) {
                this.updateCurrentValue(newValue);
            } else {
                // Volver a null = "sin mes elegido": el trigger muestra el
                // placeholder y la grilla no resalta ningún mes.
                this.selectedDate = null;
            }
        },
        rangeValue(newValue) {
            if (this.isRange) this.applyRangeValue(newValue);
        },
        disabledFrom(newValue) {
            if (!this.isRange) this.updateCurrentValue(newValue);
            this.initialDate = this.isRange ? newValue : this.selectedDate;
        },
    },
    mounted() {
        // `data()` ya seedea rangeStart/rangeEnd/currentYear desde las props,
        // así que el primer paint sale bien. Mounted sólo ata `initialDate`
        // (ancla del disableOld/New) y resetea selectedDate del single cuando
        // viene un Date posta — los watchers reactivos cubren cambios posteriores.
        if (!this.isRange && this.value) {
            this.updateCurrentValue(this.value);
        }
        this.initialDate = this.disabledFrom;
    },
    beforeDestroy() {
        this.removeFixedListeners();
    },
    methods: {
        // Calcula la posición fixed del menú desde el rect del trigger. Corre
        // sincrónico en el watcher de isOpen (antes del render del v-if), así
        // el primer paint ya sale posicionado — sin flash en (0,0). Las
        // dimensiones son estimadas porque el menú aún no está montado:
        // 240px de calendar-container + 24px padding + 2px borde de ancho;
        // header + grilla 3×4 + paddings de alto.
        applyFixedPosition() {
            const MENU_WIDTH = 266;
            const MENU_HEIGHT = 250;
            const GAP = 6;
            const MARGIN = 8;
            const rect = this.$refs.dropdown.getBoundingClientRect();
            const rawLeft = this.alignRight ? rect.right - MENU_WIDTH : rect.left;
            const left = Math.max(MARGIN, Math.min(rawLeft, window.innerWidth - MENU_WIDTH - MARGIN));
            const fitsBelow = rect.bottom + GAP + MENU_HEIGHT <= window.innerHeight;
            const fitsAbove = rect.top - GAP - MENU_HEIGHT >= 0;
            const openUp = !fitsBelow && fitsAbove;
            // top/bottom/right explícitos en "auto" para pisar los valores de
            // la clase (.dropdown-menu-select define top con calc(100%)).
            this.fixedStyle = {
                position: "fixed",
                left: `${left}px`,
                right: "auto",
                top: openUp ? "auto" : `${rect.bottom + GAP}px`,
                bottom: openUp ? `${window.innerHeight - rect.top + GAP}px` : "auto",
            };
        },
        onViewportChange(event) {
            // Scroll dentro del propio componente (si existiera) no desancla.
            if (event.target instanceof Node && this.$refs.dropdown
                && this.$refs.dropdown.contains(event.target)) {
                return;
            }
            this.closeDropdown();
        },
        removeFixedListeners() {
            window.removeEventListener("scroll", this.onViewportChange, true);
            window.removeEventListener("resize", this.onViewportChange);
        },
        toggleDropdown() {
            // En ytd cada apertura arranca en el paso de años, para que el flujo
            // sea siempre años → mes (y el usuario pueda redefinir el span).
            if (this.isYtd && !this.isOpen) this.ytdStep = "years";
            this.isOpen = !this.isOpen;
        },
        closeDropdown() {
            this.isOpen = false;
        },
        updateYear(added) {
            // En el paso "mes" del ytd el año está fijo (los chevrons van disabled).
            if (this.isYtd && this.ytdStep === "month") return;
            // En la grilla de años el chevron pagina de a 12 años (el ancho de la
            // grilla); en la de meses avanza año a año como siempre.
            if (this.showYearGrid) {
                this.yearPageStart += added * 12;
                return;
            }
            this.currentYear += added;
        },
        selectMonth(iSelectedMonth) {
            if (this.isMonthDisabled(iSelectedMonth)) return;
            // ytd paso "mes": el click fija el mes de corte y emite el combinado
            // (span de años + corte). No usa la máquina de rango (eso fue el paso 1).
            if (this.isYtd && this.ytdStep === "month") {
                this.ytdMonth = iSelectedMonth;
                this.emitYtd();
                this.isOpen = false;
                return;
            }
            const clicked = new Date(this.currentYear, iSelectedMonth, 1);
            if (this.isRange) {
                this.handleRangeClick(clicked);
                return;
            }
            this.$emit('input', clicked);
            this.isOpen = false;
            this.selectedDate = clicked;
            this.$emit("date-change", clicked);
        },
        // Click flow del modo range — implementa la regla acordada con el
        // usuario: cuando hay un rango completo, el próximo click reinicia
        // (nuevo start, end=null). Mientras se está completando, somos
        // permisivos para que el usuario corrija un endpoint sin tener que
        // arrancar de cero.
        handleRangeClick(clicked) {
            // Caso A: rango completo o no había nada — arrancamos uno nuevo.
            if (!this.rangeStart || (this.rangeStart && this.rangeEnd)) {
                this.rangeStart = clicked;
                this.rangeEnd = null;
                this.pickingPhase = "to";
                return;
            }
            // Caso B: hay start y todavía no end.
            if (clicked.getTime() < this.rangeStart.getTime()) {
                // Click anterior al start → corregimos el start (no cerramos).
                this.rangeStart = clicked;
                return;
            }
            // Click igual o posterior al start → cerramos el rango.
            this.rangeEnd = clicked;
            this.pickingPhase = "from";
            // ytd: cerrar el rango de años NO emite ni cierra el dropdown —
            // avanza al paso del mes de corte, fijando el año visible al `to`
            // (de ahí cuelga el disable de meses futuros del año en curso).
            if (this.isYtd) {
                this.ytdStep = "month";
                this.currentYear = this.rangeEnd.getFullYear();
                this.hoveredMonth = null;
                return;
            }
            this.isOpen = false;
            const payload = { from: this.rangeStart, to: this.rangeEnd };
            this.$emit("range-input", payload);
            this.$emit("range-change", payload);
        },
        // Emite el resultado combinado del ytd: span de años (Ene 1 de cada
        // endpoint) + endMonth = (año `to`, mes de corte). El padre lo desdobla
        // en update-year-range + update-end-month.
        emitYtd() {
            const fromYear = this.rangeStart.getFullYear();
            const toYear = this.rangeEnd.getFullYear();
            this.$emit("ytd-change", {
                from: new Date(fromYear, 0, 1),
                to: new Date(toYear, 0, 1),
                endMonth: new Date(toYear, this.ytdMonth, 1),
            });
        },
        updateCurrentValue(valueEntered) {
            this.selectedDate = new Date(valueEntered.getFullYear(), valueEntered.getMonth(), 1)
            this.currentYear = this.selectedDate.getFullYear()

        },
        // Sincroniza el state interno del modo range con la prop `rangeValue`.
        // Llamado en mounted + watcher de la prop. `currentYear` se posiciona
        // en el endpoint más reciente para que la primera vista del grid
        // muestre el contexto del usuario.
        applyRangeValue(rv) {
            if (!rv) {
                this.rangeStart = null;
                this.rangeEnd = null;
                this.pickingPhase = "from";
                return;
            }
            this.rangeStart = rv.from
                ? new Date(rv.from.getFullYear(), rv.from.getMonth(), 1)
                : null;
            this.rangeEnd = rv.to
                ? new Date(rv.to.getFullYear(), rv.to.getMonth(), 1)
                : null;
            this.pickingPhase = this.rangeStart && this.rangeEnd ? "from" : "to";
            const focus = this.rangeEnd || this.rangeStart;
            if (focus) {
                this.currentYear = focus.getFullYear();
                // El span de años se navega por páginas de 12 tanto en granularidad
                // "year" como en el paso de años del ytd.
                if (this.isYear || this.isYtd) {
                    this.yearPageStart = focus.getFullYear() - (focus.getFullYear() % 12);
                }
            }
        },
        isMonthDisabled(iMonth) {
            const selectedDate = new Date(this.currentYear, iMonth, 1)
            // Piso por minDate (primer mes con datos): meses anteriores fuera.
            if (this.minMonth && selectedDate.getTime() < this.minMonth.getTime()) {
                return true;
            }
            if (!this.initialDate) return false;

            return (this.disableOldDates && selectedDate.getTime() < this.initialDate.getTime() ||
                this.disableNewerDates && selectedDate.getTime() > this.initialDate.getTime())
        },
        isMonthSelected(iMonth) {
            // ytd paso "mes": resalta el mes de corte elegido (el resto de los
            // helpers de rango quedan inertes en este paso).
            if (this.isYtd) return this.ytdStep === "month" && this.ytdMonth === iMonth;
            if (this.isRange) return false;
            if (!this.selectedDate) return false;
            const isYearSelected = this.currentYear == this.selectedDate.getFullYear()
            return isYearSelected && iMonth == this.selectedDate.getMonth()
        },
        // Helpers del highlight del modo range. Los tres se computan contra el
        // `currentYear` actual del grid — sin esto, navegar a otro año pintaría
        // los mismos meses como si fueran from/to.
        //
        // Para el preview de hover durante selección (rangeStart sin rangeEnd
        // + pickingPhase === 'to'), tratamos `hoveredMonth` como un `to`
        // virtual: el grid pinta el rango propuesto en tiempo real para que
        // el usuario sepa qué va a confirmar antes de clickear.
        isMonthFrom(iMonth) {
            // En ytd la grilla de meses es el corte (single), no un rango: sus
            // endpoints son AÑOS (rangeStart/End), no aplican al highlight de mes.
            if (this.isYtd) return false;
            return this.isRange
                && !!this.rangeStart
                && this.rangeStart.getFullYear() === this.currentYear
                && this.rangeStart.getMonth() === iMonth;
        },
        isMonthTo(iMonth) {
            if (this.isYtd) return false;
            if (!this.isRange) return false;
            // Real `to` si el rango está cerrado; si no, miramos el preview.
            const endpoint = this.rangeEnd || this.previewEnd;
            if (!endpoint) return false;
            return endpoint.getFullYear() === this.currentYear
                && endpoint.getMonth() === iMonth;
        },
        isMonthInRange(iMonth) {
            if (this.isYtd) return false;
            if (!this.isRange || !this.rangeStart) return false;
            const endpoint = this.rangeEnd || this.previewEnd;
            if (!endpoint) return false;
            const cell = new Date(this.currentYear, iMonth, 1).getTime();
            const startMs = this.rangeStart.getTime();
            const endMs = endpoint.getTime();
            // Estrictamente entre los endpoints — los extremos se pintan con
            // is-from / is-to (estilo dark) y no doblan con is-in-range.
            return cell > startMs && cell < endMs;
        },
        // Preview del segundo endpoint mientras el usuario hovea durante una
        // selección parcial. Vacío si:
        //  - el modo no es range
        //  - todavía no hay un start
        //  - el rango ya está cerrado
        //  - el hover cae sobre o antes del start (no se puede cerrar hacia atrás)
        onMonthHover(iMonth) {
            if (!this.isRange || !this.rangeStart || this.rangeEnd) return;
            const candidate = new Date(this.currentYear, iMonth, 1);
            if (candidate.getTime() <= this.rangeStart.getTime()) {
                this.hoveredMonth = null;
                return;
            }
            this.hoveredMonth = candidate;
        },
        onMonthLeave() {
            this.hoveredMonth = null;
        },
        // --- Granularidad "year" ---
        // El click selecciona un año (endpoint normalizado a Ene 1).
        // `handleRangeClick` compara timestamps, así que la máquina de estados
        // del rango (start → to → reinicio) se reusa sin cambios.
        selectYear(year) {
            if (this.isYearDisabled(year)) return;
            this.handleRangeClick(new Date(year, 0, 1));
        },
        isYearDisabled(year) {
            // Piso por minDate: años enteros anteriores al primer dato, fuera.
            if (this.minMonth && year < this.minMonth.getFullYear()) return true;
            if (!this.initialDate) return false;
            return (this.disableOldDates && year < this.initialDate.getFullYear())
                || (this.disableNewerDates && year > this.initialDate.getFullYear());
        },
        // Helpers de highlight del rango de años — comparan sólo por año. Para
        // el preview de hover (rangeStart sin rangeEnd) tratamos `hoveredMonth`
        // como un `to` virtual, igual que en la grilla de meses.
        isYearFrom(year) {
            return !!this.rangeStart && this.rangeStart.getFullYear() === year;
        },
        isYearTo(year) {
            const endpoint = this.rangeEnd || this.previewEnd;
            return !!endpoint && endpoint.getFullYear() === year;
        },
        isYearInRange(year) {
            if (!this.rangeStart) return false;
            const endpoint = this.rangeEnd || this.previewEnd;
            if (!endpoint) return false;
            return year > this.rangeStart.getFullYear() && year < endpoint.getFullYear();
        },
        onYearHover(year) {
            if (!this.rangeStart || this.rangeEnd) return;
            if (year <= this.rangeStart.getFullYear()) {
                this.hoveredMonth = null;
                return;
            }
            this.hoveredMonth = new Date(year, 0, 1);
        },
    },
};
</script>
<style lang="scss" scoped>
// Transición compartida — micro-interacción "whisper" del system. Animar más
// largo le da peso de juguete; este componente es un instrumento, no un toy.
$transition_micro: 140ms cubic-bezier(0.4, 0, 0.2, 1);

// Temas del trigger single-mode. Cada uno define las CSS vars que el chip
// consume — `--background-color`, `--text-color`, etc. — para que el mismo
// markup pueda adoptar la identidad visual del contexto. Los valores caen a
// tokens del sistema de financial reports cuando hay match semántico; cuando
// no, hex local (porque alert_theme y classification_theme tienen identidad
// propia fuera del feature de reports).
.default_theme {
    --bg-color: var(--report-primary, #002e33);
    --bg-color-hover: var(--report-primary-hover, #185555);
    --bg-color-active: var(--report-primary-hover, #185555);
    --border-color: var(--report-primary, #002e33);
    --text-color: white;
    --padding-vert: 10px;
    --padding-horz: 14px;
    --height: 42px;
}

.classification_theme {
    --bg-color: var(--report-bg, white);
    --bg-color-hover: var(--report-surface, #f9fafb);
    --bg-color-active: var(--report-divider-soft, #f3f4f6);
    --border-color: var(--report-border, #e5e7eb);
    --text-color: var(--report-text-muted-strong, #4b5563);
    --padding-vert: 9px;
    --padding-horz: 12px;
    --height: 40px;
}

// Alert theme conserva su paleta amber propia — vive fuera del flujo de
// financial reports y tiene identidad semántica (warning) que no se debería
// teñir con los tokens del report.
.alert_theme {
    --bg-color: #FFFAEB;
    --bg-color-hover: #FCEFC7;
    --bg-color-active: #FDDC8A;
    --border-color: #FEC84B;
    --text-color: #92400E;
    --padding-vert: 9px;
    --padding-horz: 12px;
    --height: 40px;
}

.custom-select-container {
    position: relative;
}

.custom_label {
    font-size: 13px;
    color: var(--report-text-muted-strong, #4b5563);
    display: block;
    font-weight: 500;
}

.label_margin {
    margin-bottom: 6px;
}

// Chip del single-mode (todos los tabs excepto Ventas + clasificación de
// gastos/costos/etc.). Border + surface shift en hover — sin transforms.
.dropdown_label_select {
    background-color: var(--bg-color);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: var(--padding-vert) var(--padding-horz);
    color: var(--text-color);
    user-select: none;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    height: var(--height);
    transition: background-color $transition_micro, border-color $transition_micro;

    &:hover {
        background-color: var(--bg-color-hover);
    }
}

.dropdown_label_select_clicked,
.dropdown_label_select_clicked:hover {
    background-color: var(--bg-color-active);
}

.actual_button_arrow_down {
    flex-shrink: 0;
    align-self: center;
    width: 18px;
    height: 18px;
    color: currentColor;
    opacity: 0.85;
}

.custom-dropdown {
    position: relative;
    width: 100%;
    color: inherit;
}

.selected-label {
    line-height: 1.2;
}

// Trigger compacto del modo range (Ventas). Vive embebido al lado de los
// chips de período sin imitar su tratamiento (no es un selector activable);
// es un "field" con su propia identidad: border-only en reposo, accent fill
// cuando está abierto. La capa hover es surface, no accent, porque accent se
// reserva para el estado abierto.
.range_trigger {
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    padding: 7px 12px;
    background: var(--report-bg, white);
    border: 1px solid var(--report-border, #e5e7eb);
    border-radius: 8px;
    color: var(--report-text, #111827);
    cursor: pointer;
    line-height: 1.2;
    transition: background-color $transition_micro, border-color $transition_micro,
        box-shadow $transition_micro;

    &:hover {
        background: var(--report-surface, #f9fafb);
        border-color: var(--report-border-soft, #d1d5db);
    }

    &:focus-visible {
        outline: none;
        border-color: var(--report-accent-border, #aaf1f7);
        box-shadow: 0 0 0 3px rgba(170, 241, 247, 0.4);
    }

    // Estado abierto: snap instantáneo de border + background a los tokens
    // accent. Sin transición — el dropdown ya animará por su cuenta y queremos
    // un único motion visible. Cuando se cierra (`.is-open` se quita), la
    // transición base de la regla anterior se aplica y vuelve a los tokens de
    // reposo suavemente (mismo timing que el fade-out del dropdown).
    &.is-open {
        border-color: var(--report-accent-border, #aaf1f7);
        background: var(--report-accent-bg, #edfdfe);
        transition: none;
    }
}

.range_trigger_label {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--report-text, #111827);
}

.range_trigger_dates {
    font-size: 12px;
    font-weight: 400;
    color: var(--report-text-muted, #6b7280);
    white-space: nowrap;
    // Cuando viene "—" (sin selección aún) lo bajamos otro tono para que se
    // lea como placeholder, no como dato. Los placeholders no son textos
    // legítimos — son ausencia con forma.
    .range_trigger.is-open &,
    .range_trigger:not(.is-open) & {
        font-variant-numeric: tabular-nums;
    }
}

.range_sep {
    color: var(--report-text-muted-soft, #9ca3af);
    margin: 0 2px;
}

// Dropdown del calendar (mes-picker). Whisper-quiet shadow + border definido
// — mismo tratamiento que las cards del sistema (Headline, Stores
// distribution, etc.). Lo subimos a `--report-bg` puro (un nivel sobre el
// trigger que está en surface) para mantener la jerarquía de elevación.
.dropdown-menu-select {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: fit-content;
    background-color: var(--report-bg, white);
    border: 1px solid var(--report-border, #e5e7eb);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.04),
        0 8px 24px rgba(10, 13, 20, 0.08);
    z-index: 9999;
    padding: 12px;
    // Hint al compositor del navegador: estos channels van a animarse en
    // cada apertura/cierre. Evita un layout-trigger en el primer frame.
    will-change: opacity;
}

.dropdown-menu-select.is-right {
    left: auto;
    right: 0;
}

// Transiciones bien cortas — el ojo no alcanza a leer pasos intermedios y
// la animación se siente como un "pop" suave en lugar de un viaje. Por encima
// de ~80ms los micro-cambios de los hijos (month cells, year nav) compiten
// con el fade del padre y producen la sensación entrecortada.
//
// Open un poco más corto que close: al abrir el usuario quiere ver el
// contenido ya; al cerrar tiene más tolerancia a un cierre apenas más lento.
.slide-enter-active {
    transition: opacity 50ms linear;
}

.slide-leave-active {
    transition: opacity 70ms linear;
}

.slide-enter,
.slide-leave-to {
    opacity: 0;
}

// El calendar tiene un ancho fijo para que las 3 columnas del grid no se
// estiren a `1fr` libre. El número (236px) es el mínimo cómodo para que
// "Septiembre" / "Noviembre" / "Diciembre" entren en sus celdas sin cortar:
// 3 cols × 72px de cell + 2 × 6px de gap = 228px, con margen para que la
// alineación visual no quede pegada al borde.
.calendar-container {
    width: 240px;
    text-align: center;
}

// Cabecera del año con dos chevrons simétricos. La estructura es estable
// (los chevrons siempre presentes — disabled cuando aplica) para que el
// layout no salte al alcanzar un borde temporal. `space-between` con el
// container ya angosto deja los chevrons y el año visualmente conectados.
.calendar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.year_label {
    font-size: 13px;
    font-weight: 600;
    color: var(--report-text, #111827);
    letter-spacing: 0.01em;
    font-variant-numeric: tabular-nums;
}

// Icon button del año — usa el container pattern del system: background
// transparente por default, surface en hover. El icon-only se beneficia de
// un fondo en hover para sentirse touchable.
.year_nav_btn {
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 3px;
    cursor: pointer;
    color: var(--report-text-muted-strong, #4b5563);
    transition: background-color $transition_micro, color $transition_micro;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background: var(--report-surface, #f9fafb);
        color: var(--report-text, #111827);
    }

    &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px rgba(170, 241, 247, 0.4);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
}

.year_nav_icon {
    width: 14px;
    height: 14px;
    color: currentColor;
}

// Grid 3×4 — `1fr` puro contra el ancho fijo del container ya constrained.
// Sin minmax porque el container ya es lo bastante chico para que las cells
// no se inflen, pero igual lo suficientemente grande para que "Septiembre"
// quepa dentro del cell sin recortar.
.calendar-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

// Mes como `<button>` — reset del default browser. Border-only en reposo +
// surface fill en hover. Sin transforms. La elevación se siente por color,
// no por movimiento. `min-width: 0` permite que el cell se compacte hasta el
// ancho del grid track sin desbordar; el text-align center mantiene la
// composición visual.
.month-cell {
    appearance: none;
    -webkit-appearance: none;
    min-width: 0;
    padding: 8px 4px;
    background: var(--report-bg, white);
    border: 1px solid var(--report-border, #e5e7eb);
    border-radius: 6px;
    color: var(--report-text-strong-2, #374151);
    text-align: center;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background-color $transition_micro, border-color $transition_micro,
        color $transition_micro;

    &:hover:not(:disabled):not(.month-cell-from):not(.month-cell-to) {
        background: var(--report-surface, #f9fafb);
        border-color: var(--report-border-soft, #d1d5db);
    }

    &:focus-visible {
        outline: none;
        border-color: var(--report-accent-border, #aaf1f7);
        box-shadow: 0 0 0 3px rgba(170, 241, 247, 0.4);
    }

    &:disabled,
    &.is-disabled-local {
        color: var(--report-text-muted-soft, #9ca3af);
        cursor: not-allowed;
        background: transparent;
        border-color: transparent;
    }
}

// Mes activo en modo single — usa el mismo tratamiento que from/to del range
// para consistencia visual entre modos. El usuario no debería tener que
// re-aprender el highlight al cambiar de modo.
.month-cell-activated {
    background: var(--report-primary, #0d3c3f);
    border-color: var(--report-primary, #0d3c3f);
    color: white;

    &:hover {
        background: var(--report-primary-hover, #185555);
        border-color: var(--report-primary-hover, #185555);
    }
}

// Modo range — los dos extremos en primary del theme (continuidad con
// month-cell-activated). El fill in-range es accent-bg, no surface, porque
// debe distinguirse del hover y leerse como "incluido en el rango".
.month-cell-from,
.month-cell-to {
    background: var(--report-primary, #0d3c3f);
    border-color: var(--report-primary, #0d3c3f);
    color: white;

    &:hover {
        background: var(--report-primary-hover, #185555);
        border-color: var(--report-primary-hover, #185555);
    }
}

.month-cell-in-range {
    background: var(--report-accent-bg, #edfdfe);
    border-color: var(--report-accent-bg, #edfdfe);
    color: var(--report-accent-text, #157389);

    &:hover {
        border-color: var(--report-accent-border, #aaf1f7);
    }
}

// Preview (hover sobre un mes futuro durante una selección parcial). Una
// muesca más suave que el rango real para que se lea como "esto pasaría si
// confirmás" y no como "esto es lo seleccionado". `is-preview` se aplica en
// conjunto con month-cell-to / month-cell-in-range — su única tarea es
// bajar la opacidad del fill.
.month-cell.is-preview {
    opacity: 0.6;
}

// Rotaciones de los chevrons del year nav.
.rotateLeft {
    transform: rotate(90deg);
}

.rotateRight {
    transform: rotate(270deg);
}
</style>